import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, Signal, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HistoryState } from '../../../models/patient-history.models';
import { selectSeReportIsAnyExaminationActive } from '../../../report/report.selectors';
import { RootState } from '../../../root-store/root.state';
import { NotificationService } from '../../../services/notification.service';
import { PatientReportHistoryService } from '../../../services/rest/patient-report-history.service';
import { SeSideService } from '../../se-side.service';
import { SeSideComponentBase } from '../se-side-components-models';
import { SeSideSnackbarComponent } from '../se-side-snackbar/se-side-snackbar.component';
import { SeSideSnackbarStateService } from '../services/se-side-snackbar-state.service';
import { SeSideReportHistoryItemComponent } from './se-side-report-history-item/se-side-report-history-item.component';

@Component({
    selector: 'se-side-report-history',
    standalone: true,
    templateUrl: './se-side-report-history.component.html',
    styleUrl: './se-side-report-history.component.scss',
    imports: [
        SeSideReportHistoryItemComponent,
        CommonModule,
        MatProgressSpinnerModule,
        TranslateModule,
        MatFormFieldModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        SeSideSnackbarComponent,
        MatTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SeSideSnackbarStateService]
})
export class SeSideReportHistoryComponent extends SeSideComponentBase implements OnInit, OnDestroy {
    @HostBinding('style.display')

    private notificationService: NotificationService = inject(NotificationService);
    public reportHistoryService: PatientReportHistoryService = inject(PatientReportHistoryService);
    private seSideService: SeSideService = inject(SeSideService);
    private snackbarState: SeSideSnackbarStateService = inject(SeSideSnackbarStateService);
    private store: Store<RootState> = inject(Store<RootState>);
    private translate: TranslateService = inject(TranslateService);

    private subscriptions: Subscription[] = [];
    actorErrors = this.reportHistoryService.reportHistoryErrors;
    expandedItemId: string | null = null;
    isAnyExaminationActive: boolean = false;
    modalities: Signal<string[]> = this.reportHistoryService.modalitiesListComputed;
    reportHistoryMessage: string = '';

    /** COMPUTED */
    /**
     * Man mano che reportHistoryArray si popola, mappo i dati in una specie di EPREVENT che sia utile per la sidenav
     */
    eprEvents = computed(() => {
        const allReports = this.reportHistoryService.reportHistoryArray() ?? [];

        return allReports.flatMap(report =>
            (report.reports ?? []).map(el => ({
                ...el,
                selected: el.id === this.expandedItemId,
                examsDescriptions: el.examinations.map(exam => exam.exam),
                referringPhysiciansName: el.referringPhysicians.map(el => el.fullName),
                reportKey: el.sopInstanceUID,
            }))
        );
    });

    /**
     * Filtra gli eprEvents in base alle modalità selezionate nella combo dei filtri
     */
    filteredEprEvents = computed(() => {
        const allReports = this.eprEvents();
        const selected = this.reportHistoryService.selectedModalities();
        if (!selected.length) return allReports;

        return allReports.filter(ev => ev.examinations?.some(ex => selected.includes(ex.modality!)));
    });

    /**
     * Uso un computed per determinare quando far partire lo spinner
     * deve partire solo quando non ci sono dati, ma all'arrivo della prima risposta (nel caso ci siano N chiamate), deve fermarsi
     */
    isLoadingComputed = computed(() => {
        const payloads = this.reportHistoryService.reportHistoryPayloads();
        const array = this.reportHistoryService.reportHistoryArray();

        const payloadsArrivati = payloads ? payloads?.length > 0 : false;
        const risultatiArrivati = array?.length > 0;

        return payloadsArrivati && !risultatiArrivati;
    });

    constructor() {
        super();
        this.reportHistoryMessage = this.translate.instant('NoResults');
        this.isLoading.next(false);

        /**
         * Effect per la gestione degli errori di caricamento della report history
         * aggiorna lo stato di errore del tab, mostra una snackbar e notifica in campanella
         */
        effect(() => {
            const state = this.reportHistoryService.reportHistoryState();

            switch (state) {
                case HistoryState.INCOMPLETE:
                case HistoryState.ERROR:
                    this.seSideService.setTabError('reportHistory', true);
                    this.snackbarState.show(this.translate.instant('ReportHistoryFail'), 'error');
                    let message = this.translate.instant('ReportHistoryActorsPartialError', {
                        p0: this.reportHistoryService.errorCount(),
                        p1: this.reportHistoryService.totalActors(),
                        p2: this.reportHistoryService.reportHistoryErrors()
                            .map(e =>
                                typeof e.actorDetail === 'string'
                                    ? e.actorDetail
                                    : e.actorDetail?.description
                            )
                            .join(', '),
                    });
                    this.notificationService.error(message, this.translate.instant('ReportHistoryFailTitle'));
                    break;

                case HistoryState.SUCCESS_NO_RESULT:
                case HistoryState.SUCCESS:
                    this.seSideService.setTabError('reportHistory', false);
                    this.snackbarState.clear();
                    break;
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    ngOnInit(): void {
        this.listenForStoreEvents();
    }

    private listenForStoreEvents() {
        let subscription = this.store.select(selectSeReportIsAnyExaminationActive).subscribe((isAnyExaminationActive) => {
            // Metodo per capire se siamo in WL ( = false) o dentro una CP ( = true)
            this.isAnyExaminationActive = isAnyExaminationActive;
        });
        this.subscriptions.push(subscription);
    }

    _onReportHistorySelectionChanged(selectedItem: any) {
        this.filteredEprEvents().forEach((item) => {
            item.selected = (item.id == selectedItem.id);
        });
        if (this.expandedItemId === selectedItem.id) {
            this.expandedItemId = null;
        } else {
            this.expandedItemId = selectedItem.id;
        }
    }

    _onFilterChange(event: MatSelectChange) {
        this.reportHistoryService.setSelectedModalities(event.value);

        //Deseleziono l’elemento selezionato se non è più visibile dopo il filtraggio
        const stillVisible = this.filteredEprEvents().some(it => it.id === this.expandedItemId);
        if (!stillVisible) {
            this.expandedItemId = null;
        }
    }

    _resetComboSelectedValue(event: PointerEvent) {
        event.stopPropagation();
        this.reportHistoryService.setSelectedModalities([]);
        this.expandedItemId = null;
    }

    get reportHistoryTooltip(): string {
        switch (this.reportHistoryService.reportHistoryState()) {
            case HistoryState.INCOMPLETE:
            case HistoryState.ERROR:
                return this.translate.instant(
                    'ReportHistoryFail'
                );
            case HistoryState.SUCCESS:
            case HistoryState.SUCCESS_NO_RESULT:
                return this.translate.instant(
                    'ReportHistoryComplete'
                );
            default:
                return '';
        }
    }
}
